<script setup>
import { useRouter } from 'vue-router';
import { computed, ref, reactive, watch } from 'vue';
import { useAuthComponents, useAuthActions } from '@auth';
import { i18n, PopUpManager as PopUp, verifyField } from '@/Ship';
import { useProfileActions } from '@profile';

const { signUp, signIn } = useAuthActions();
const getCountryResource = useProfileActions('getCountryResource');

const PasswordInput = useAuthComponents('PasswordInput');

const countries = getCountryResource();
const router = useRouter();

const isAllow = ref();
const isComplex = ref(false);

const payload = reactive({
    first_name: '',
    last_name: '',
    phone: '',
    country_code: '',
    email: '',
    password: '',
    app: process.env.VUE_APP_NAME
});

const errors = reactive({
    first_name: null,
    last_name: null,
    phone: null,
    email: null,
    country_code: null,
    password: null,
});

const firstName = computed({
    get: () => payload.first_name,
    set: value => {
        payload.first_name = value;
        errors.first_name = verifyField(value, ['require', 'minLength:2']);
        return true;
    }
});

const lastName = computed({
    get: () => payload.last_name,
    set: value => {
        payload.last_name = value;
        errors.last_name = verifyField(value, ['require', 'minLength:2']);
        return true;
    }
});

const email = computed({
    get: () => payload.email,
    set: value => {
        payload.email = value;
        errors.email = verifyField(value, ['require', 'email']);
        return true;
    }
});

const countryCode = computed({
    get: () => payload.country_code,
    set: value => {
        payload.country_code = value;
        errors.country_code = verifyField(value, ['require']);
        return true;
    }
});

const phone = computed({
    get: () => payload.phone,
    set: value => {
        payload.phone = value;
        errors.phone = verifyField(value, ['require', 'phone']);
        return true;
    }
});

const password = computed({
    get: () => payload.password,
    set: value => {
        payload.password = value;
        errors.password = verifyField(value, ['minLength:8']);
        return true;
    }
});

watch([errors, payload], () => {
    isAllow.value = true;
    for (const key in errors) {
        if (errors[key] !== null || !payload[key].length) {
            isAllow.value = false;
            break;
        }
    }
});

function registration() {
    if (isAllow.value) {
        signUp(payload).then(() => {
            signIn(payload.email, payload.password).then(() => {
                router.push({ name: 'Trading', params: router.currentRoute.value.params });
            })
        }).catch(({response}) => {
            PopUp.open(require('@auth/components/signUpInfoPopUp.vue'))
                .setCaption(i18n.global.t('base.error'))
                .setMessage(`<p class="message"><b>${response.data.message}</b></p> <a href="/reset-password" style="color: #22b56aff">Use the password recovery form</a>`)
                .setPayload({payload: {mainText: '', additionText: ''}});
        });
    }
}
</script>

<template>
    <form class="sign-up" v-on:submit.prevent="registration">

        <g-caption size="3" weight="600">
            <router-link v-bind:to="{ name: 'SignIn', params: { locale: $i18n.locale } }" tag="a" class="link sign-in">
                {{ $t('auth.sign_in', 'Sign In') }}
            </router-link>

            {{ $t('auth.sign_up', 'Sign Up') }}
        </g-caption>

        <div class="subtitle">{{ $t('auth.sign_sub_title') }}</div>

        <label class="field">
            <span>{{ $t('profile.first_name', 'First Name') }}</span>
            <g-input v-model="firstName" v-bind:error="errors.first_name"/>
        </label>

        <label class="field">
            <span>{{ $t('profile.last_name', 'Last Name') }}</span>
            <g-input v-model="lastName" v-bind:error="errors.last_name"/>
        </label>

        <label class="field">
            <span>{{ $t('profile.email', 'Email') }}</span>
            <g-input v-model="email" type="email" v-bind::error="errors.email"/>
        </label>

        <label class="field">
            <g-phone-input v-model="phone"
                           v-model:country="countryCode"
                           v-bind:countries="countries.state"
                           v-bind:error="errors.phone"
                           v-bind:label="$t('profile.phone', 'Phone')"/>
        </label>
        <hr class="separator">

        <div class="subtitle">{{ $t('auth.reset_sub_title_password') }}</div>

        <password-input v-model="password"
                        v-model:complex="isComplex"
                        v-bind:label="$t('profile.password')"
                        v-bind:error="errors.password"/>


        <g-button v-bind:disabled="!isAllow">
            {{ $t('auth.create_account', 'Create Account') }}
        </g-button>

        <div>
            {{ $t('auth.sign_have_account') }}
            <router-link :to="{ name: 'SignIn', params: { locale: $i18n.locale } }" tag="a" className="link">
                {{ $t('auth.sign_in', 'Sign In') }}
            </router-link>
        </div>
    </form>
</template>

<style lang="scss" scoped>
.sign-up {
    padding: 0 18px;
    max-width: 440px;
    width: 100%;
    gap: 10px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
}

.g-caption {
    width: 100%;
    margin-bottom: 25px;
    display: flex;
    justify-content: space-between;
    color: var(--title-text-color, $title-text-color);

    & > .sign-in {
        width: 120px;
        font-size: 95%;
        transition: font-size 0.3s;

        &:hover {
            font-size: 105%;
        }
    }
}

.subtitle {
    font-size: 1.1rem;
    margin-bottom: 5px;
    color: var(--title-text-color, $title-text-color);
}

.g-button {
    width: 100%;
    background-color: var(--login-btn, $login-btn);

    &:hover {
        background-color: var(--login-btn-hover, $login-btn-hover);
    }
}

.link {
    font-size: 106%;
    font-weight: 600;
    margin-left: 5px;
    color: var(--success, $success);
    transition: color 0.2s;

    &:hover {
        color: var(--success-hover, $success-hover);
    }
}

.separator {
    flex-basis: 100%;
    height: 1px;
    border: none;
    margin-top: 20px;
    background-color: var(--separator-color, $separator-color);
}

.field,
.password-input {
    flex-basis: 100%;

    & > span {
        font-size: 1.1rem;
        font-weight: 600;
        color: var(--title-text-color, $title-text-color) !important;
    }
}
</style>
